/**
 * Formatting utility functions
 */

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'XAF')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'XAF') => {
  if (amount === null || amount === undefined) return '0';
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) return '0';
  
  // Format number with thousand separators
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numAmount);
  
  return `${formatted} ${currency}`;
};

/**
 * Format date string
 * @param {string} dateString - ISO date string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
};

/**
 * Format date and time
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format number with thousand separators
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  
  const number = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(number)) return '0';
  
  return new Intl.NumberFormat('en-US').format(number);
};

/**
 * Format payment method name
 * @param {string} method - Payment method code
 * @returns {string} Formatted payment method name
 */
export const formatPaymentMethod = (method) => {
  const methods = {
    mobile_money: 'Mobile Money',
    bank_transfer: 'Bank Transfer',
    cash: 'Cash',
    card: 'Card',
    check: 'Check',
    other: 'Other',
  };
  
  return methods[method] || method?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || method;
};

/**
 * Format status with proper capitalization
 * @param {string} status - Status string
 * @returns {string} Formatted status
 */
export const formatStatus = (status) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};
